import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// =============================================================================
// Single-position cycle history & order drill-down client.
// Field sets pinned in spec-driven/single_position_cycle_history_and_order_drill_down/
// wire-contracts.md; BigDecimal values arrive as raw JSON numbers on these endpoints
// (api-local JsonUtils has no BigDecimal-as-string module), so money fields are `number`.
// Bound to API_CONFIG.tradeGroupBaseUrl (endpoints live on the trade-groups REST API).
// =============================================================================

export interface ResponseMetadata {
  total?: number;
  page?: number;
  pageSize?: number;
  timestamp: string;
  requestId?: string;
}

interface SingleResponse<T> {
  data: T;
  metadata: ResponseMetadata;
}

/**
 * The breakeven trail's effective config for a Support-Retest group, resolved live through
 * coalesce(group, product, global). Group-scoped and cycle-independent, so it answers "is the trail
 * on for this group?" before any cycle exists — no database column can, because the override columns
 * hold NULL for "inherit" and the effective value is never persisted.
 */
export interface SupportRetestTrailConfigView {
  trailingStopEnabled: boolean | null;
  trailingStopEnabledTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  trailingStopActivationBasis: string | null;
  trailingStopActivationBasisTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  trailingStopDistanceFraction: number | null;
  trailingStopDistanceFractionTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
}

export interface CycleGroupSummary {
  cycleCount: number | null;
  winCount: number | null;
  lossCount: number | null;
  cumulativePnl: number | null;
  state: string | null;
  /** Populated only for SUPPORT_RETEST_DIP_LADDER groups; null for every other strategy. */
  supportRetestTrailConfig: SupportRetestTrailConfigView | null;
}

export interface CycleSummary {
  cycleId: string;
  cycleNumber: number | null;
  cycleState: string | null;
  entryTime: string | null;
  entryPrice: number | null;
  /**
   * Quote deployed on the entry side, summed from the exchange fills across all entry legs — the
   * denominator for P&L percent. Null when no entry leg filled.
   */
  deployedCapital: number | null;
  exitTime: string | null;
  exitPrice: number | null;
  stopPrice: number | null;
  realizedPnl: number | null;
  isWinner: boolean | null;
  exitReason: string | null;
  remainingPositionSize: number | null;
  lowWaterMark: number | null;
  highWaterMark: number | null;
  /**
   * True when the water marks were computed live over [entry, now] for a cycle still in position,
   * rather than read from the columns stamped once at CYCLE_COMPLETE. A provisional pair can still
   * widen; a non-provisional pair is final.
   */
  waterMarksProvisional: boolean;
  createdAt: string | null;
}

export interface CycleOrder {
  role: string;
  clientOrderId: string;
  orderId: string | null;
  side: string | null;
  status: string | null;
  quoteSize: number | null;
  baseSize: number | null;
  limitPrice: number | null;
  postOnly: boolean | null;
  completionPercentage: number | null;
  filledSize: number | null;
  averageFillPrice: number | null;
  filledValue: number | null;
  totalFees: number | null;
  valueAfterFees: number | null;
  numberOfFills: number | null;
  fillTime: string | null;
  stopPrice: number | null;
  stopDirection: string | null;
}

// --- Discriminated context union (keyed on `type` = tradeGroupType) ---

export interface MaCrossoverContext {
  type: 'MA_CROSSOVER';
  entryFastMa: number | null;
  entrySlowMa: number | null;
  entryMaSeparationPct: number | null;
  entryFastSlopePct: number | null;
  entrySlowSlopePct: number | null;
  entryRegime: string | null;
  exitFastMa: number | null;
  exitSlowMa: number | null;
  exitReasonDetail: string | null;
}

export interface BollingerReversionContext {
  type: 'BOLLINGER_REVERSION';
  entryRsi: number | null;
  entryLowerBand: number | null;
  entryMiddleBand: number | null;
  entryUpperBand: number | null;
  entryMarketRegime: string | null;
  entryS2Pivot: number | null;
  positionSize: number | null;
  deployedCapital: number | null;
  primaryTarget: number | null;
  extendedTarget: number | null;
  partialExitAmount: number | null;
  cycleStatus: string | null;
}

export interface RsiExtremesContext {
  type: 'RSI_EXTREMES';
  entryRsi: number | null;
  entryRegime: string | null;
  entryVolatilityState: string | null;
  entryVolatilityPercentile: number | null;
  entryTrendStrength: number | null;
  rsiOverboughtThreshold: number | null;
  profitTargetPrice: number | null;
  partialExitRsi: number | null;
  partialExitQuoteSize: number | null;
  exitRsi: number | null;
  exitQuoteSize: number | null;
}

export interface MacdReversionContext {
  type: 'MACD_REVERSION';
  entryRsi: number | null;
  entryMacdLine: number | null;
  entrySignalLine: number | null;
  entryHistogram: number | null;
  entryHistogramPrev: number | null;
  entryMarketRegime: string | null;
  entryVolatilityState: string | null;
  entryVolatilityPctile: number | null;
  entryTrendHostility: number | null;
  entryS2Pivot: number | null;
  positionSize: number | null;
  deployedCapital: number | null;
  profitTargetPrice: number | null;
  exitRsi: number | null;
  exitMacdLine: number | null;
  exitSignalLine: number | null;
  exitHistogram: number | null;
  partialExitMacdLine: number | null;
  partialExitSignalLine: number | null;
  partialExitHistogram: number | null;
  partialExitRsi: number | null;
}

export interface MacdMomentumContext {
  type: 'MACD_MOMENTUM';
  entryMacdLine: number | null;
  entrySignalLine: number | null;
  entryHistogram: number | null;
  entryHistogramPrev: number | null;
  entryRegime: string | null;
  entryRsi: number | null;
  entryConfidenceScore: number | null;
  exitMacdLine: number | null;
  exitSignalLine: number | null;
  exitHistogram: number | null;
  peakPrice: number | null;
  trailingStopActivationPrice: number | null;
  trailingStopActivated: boolean | null;
}

export interface VwapReversionContext {
  type: 'VWAP_REVERSION';
  entryVwap: number | null;
  entryUpperBand: number | null;
  entryLowerBand: number | null;
  entryBandWidth: number | null;
  entryVwapDeviationPct: number | null;
  exitVwap: number | null;
  exitQuoteSize: number | null;
  partialExitQuoteSize: number | null;
  partialExitVwap: number | null;
  stopBasis: string | null;
  effectiveAtrMultiplier: number | null;
  effectiveStopTier: string | null;
}

export interface IchimokuCloudContext {
  type: 'ICHIMOKU_CLOUD';
  entryTenkanSen: number | null;
  entryKijunSen: number | null;
  entrySenkouSpanA: number | null;
  entrySenkouSpanB: number | null;
  entryChikouSpan: number | null;
  entryCloudTop: number | null;
  entryCloudBottom: number | null;
  entrySignalComponents: string | null;
  stopLossBasis: number | null;
  exitTenkanSen: number | null;
  exitKijunSen: number | null;
  exitSenkouSpanA: number | null;
  exitSenkouSpanB: number | null;
  exitChikouSpan: number | null;
  exitCloudTop: number | null;
  exitCloudBottom: number | null;
  lastTrailingStopUpdateAt: string | null;
  finalTrailingKijun: number | null;
  cloudBreakConfirmationCloses: string | null;
  atrAtEntry: number | null;
  atrStopPrice: number | null;
  atrTargetPrice: number | null;
}

export interface PivotPointContext {
  type: 'PIVOT_POINT_TRADING';
  entryLevelType: string | null;
  entrySignalStrength: number | null;
  entryRsi: number | null;
  entryMarketRegime: string | null;
  confidenceScore: number | null;
  targetPivot: number | null;
  targetR1: number | null;
  targetR2: number | null;
}

export interface SupportRetestContext {
  type: 'SUPPORT_RETEST_DIP_LADDER';
  zoneHigh: number | null;
  zoneLow: number | null;
  representativePrice: number | null;
  targetPrice: number | null;
  invalidationPrice: number | null;
  signalGranularityMinutes: number | null;
  levelBroken: boolean | null;
  // Resolved tunable config + tier (support_retest_tunable_config_tiers REQ-004/REQ-007,
  // wire-contracts.md Boundary 2).
  targetPct: number | null;
  targetPctTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  breakThresholdFraction: number | null;
  breakThresholdFractionTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  floorBufferFraction: number | null;
  floorBufferFractionTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  // Breakeven trail (support_retest_breakeven_trailing_stop REQ-007) — resolved config plus this
  // cycle's live trail state. READ-ONLY: these render through the generic SinglePositionCycleContext
  // renderer; making them editable per group is a follow-on spec.
  trailingStopEnabled: boolean | null;
  trailingStopEnabledTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  trailingStopActivationBasis: 'POOLED_BREAKEVEN' | 'HIGHEST_LEG_BREAKEVEN' | null;
  trailingStopActivationBasisTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  trailingStopDistanceFraction: number | null;
  trailingStopDistanceFractionTier: 'GROUP' | 'PRODUCT' | 'GLOBAL' | null;
  // trailPrice is the base table's stop_loss_price. Named for the trail deliberately: SRDL places no
  // exchange-side stop order at all, so "stop price" would describe a broker order that never exists.
  trailPrice: number | null;
  trailRatchets: number | null;
  trailArmed: boolean | null;
}

export type SinglePositionCycleContext =
  | MaCrossoverContext
  | BollingerReversionContext
  | RsiExtremesContext
  | MacdReversionContext
  | MacdMomentumContext
  | VwapReversionContext
  | IchimokuCloudContext
  | PivotPointContext
  | SupportRetestContext;

export interface CycleHistoryResponse {
  applicable: boolean;
  tradeGroupType: string;
  summary: CycleGroupSummary | null;
  cycles: CycleSummary[];
}

export interface CycleHistoryResult extends CycleHistoryResponse {
  total: number;
  page: number;
  pageSize: number;
}

export interface CycleDetailResponse {
  applicable: boolean;
  tradeGroupType: string;
  cycle: CycleSummary | null;
  orders: CycleOrder[];
  context: SinglePositionCycleContext | null;
}

export interface CycleHistoryParams {
  page?: number;
  pageSize?: number;
  sort?: 'cycleNumber' | 'entryTime' | 'realizedPnl';
  dir?: 'asc' | 'desc';
}

// =============================================================================
// Auth + fetch primitive (mirrors performanceRollups.ts, bound to tradeGroupBaseUrl)
// =============================================================================

async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

async function apiFetch<T>(endpoint: string): Promise<T> {
  const url = `${API_CONFIG.tradeGroupBaseUrl}${endpoint}`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Cycle History API Error: ${response.status} - ${response.statusText}`);
    }

    return (await response.json()) as T;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') throw new Error('Request timeout - please try again');
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// =============================================================================
// Endpoint functions
// =============================================================================

export async function getCycleHistory(
  tradeGroupId: string,
  params: CycleHistoryParams = {}
): Promise<CycleHistoryResult> {
  const response = await apiFetch<SingleResponse<CycleHistoryResponse>>(
    `/v1/trade-groups/${encodeURIComponent(tradeGroupId)}/cycles${buildQuery(params as Record<string, unknown>)}`
  );
  return {
    ...response.data,
    total: response.metadata.total ?? response.data.cycles.length,
    page: response.metadata.page ?? params.page ?? 1,
    pageSize: response.metadata.pageSize ?? params.pageSize ?? 25,
  };
}

export async function getCycleDetail(
  tradeGroupId: string,
  cycleId: string
): Promise<CycleDetailResponse> {
  const response = await apiFetch<SingleResponse<CycleDetailResponse>>(
    `/v1/trade-groups/${encodeURIComponent(tradeGroupId)}/cycles/${encodeURIComponent(cycleId)}`
  );
  return response.data;
}
